import ChangeActiveHospitalModal from "./ChangeActiveHospitalModal";

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { IActiveHospital, IActiveHospitalProvider, IContext } from "./types";
import {
  getActiveHospitalStorage,
  getLastHospitalSelected,
  setActiveHospitalStorage,
} from "./utils";
import { getHospitals } from "./ChangeActiveHospitalModal/utils";
import { useAuth } from "../Auth/useAuth";
import { ROLE_CONVENIO, ROLE_DOCTOR, ROLE_HOSPITALS, ROLE_PATIENT } from "../../types/role";

export const ActiveHospitalContext = createContext<IContext>({} as IContext);

export const ActiveHospitalProvider = ({
  children,
}: IActiveHospitalProvider) => {
  const auth = useAuth();

  const getLastHospitalSelectedRequestRef = useRef<AbortController | null>(
    null,
  );

  const [activeHospital, setActiveHospital] = useState<
    IActiveHospital | undefined
  >();
  const [changeActiveHospitalIsOpen, setChangeActiveHospitalIsOpen] =
    useState<boolean>(false);

  const getLastHospitalSelectedCallback = useCallback(async () => {
    const lastHospitalSelected = await getLastHospitalSelected();
    if (lastHospitalSelected) {
      setActiveHospital({
        id: parseInt(lastHospitalSelected.id),
        name: lastHospitalSelected.name,
        permissions: lastHospitalSelected.permissions,
        uuid: lastHospitalSelected.uuid,
        isProcedenciaActive: lastHospitalSelected.isProcedenciaActive,
      });
      if (auth.user?.role_id === ROLE_HOSPITALS) {
        auth.setUser({
          ...lastHospitalSelected.user,
          permissoes: lastHospitalSelected.permissions,
          hospital: lastHospitalSelected.id,
        });
      }
      return;
    }

    if (auth.user?.status !== 5 && auth.user?.role_id !== 5) {
      setChangeActiveHospitalIsOpen(true);
    }
  }, []);

  // A stored hospital is trusted only when it is one of this user's
  // hospitals: storage can outlive the session that wrote it, and the
  // backend that serves appointments does not check the hospital itself.
  // Anything else, including a failed check, falls back to the backend.
  const restoreStoredHospitalCallback = useCallback(
    async (storedHospital: IActiveHospital) => {
      try {
        const hospitals = await getHospitals(new AbortController().signal);
        if (hospitals.some((hospital) => hospital.uuid === storedHospital.uuid)) {
          setActiveHospital(storedHospital);
          return;
        }
      } catch (error) {
        console.error("Error validating the stored active hospital:", error);
      }

      setActiveHospitalStorage(undefined);
      getLastHospitalSelectedCallback();
    },
    [getLastHospitalSelectedCallback],
  );

  useEffect(() => {
    if (
      auth.user?.role_id !== ROLE_DOCTOR &&
      auth.user?.role_id !== ROLE_PATIENT &&
      auth.user?.role_id !== ROLE_CONVENIO
    ) {
      const activeHospitalFromStorage = getActiveHospitalStorage();

      activeHospitalFromStorage && restoreStoredHospitalCallback(activeHospitalFromStorage);
      !activeHospitalFromStorage && getLastHospitalSelectedCallback();
    }

    if (
      auth.user?.role_id === ROLE_DOCTOR ||
      auth.user?.role_id === ROLE_PATIENT
    ) {
      setActiveHospital({ uuid: auth.user!.hash! });
    }
  }, [auth.user?.role_id, auth.user?.hash]);

  useEffect(() => {
    setActiveHospitalStorage(activeHospital);
  }, [activeHospital]);

  useEffect(() => {
    getLastHospitalSelectedRequestRef.current?.abort();
  }, [activeHospital, changeActiveHospitalIsOpen]);

  const changeActiveHospitalModal = useMemo(
    () =>
      auth.user?.role_id !== ROLE_DOCTOR &&
        auth.user?.role_id !== ROLE_PATIENT &&
        auth.user?.role_id !== ROLE_CONVENIO ? (
        <ChangeActiveHospitalModal
          changeActiveHospitalIsOpen={changeActiveHospitalIsOpen}
          setChangeActiveHospitalIsOpen={setChangeActiveHospitalIsOpen}
          activeHospital={activeHospital}
          setActiveHospital={setActiveHospital}
        />
      ) : (
        <></>
      ),
    [
      auth.user?.role_id,
      changeActiveHospitalIsOpen,
      setChangeActiveHospitalIsOpen,
      activeHospital,
      setActiveHospital,
    ],
  );

  return (
    <ActiveHospitalContext.Provider
      value={{ ...activeHospital, setChangeActiveHospitalIsOpen }}
    >
      {changeActiveHospitalModal}
      {children}
    </ActiveHospitalContext.Provider>
  );
};
